<template>
  <q-card class="inhdialog-container">
    <div class="inhdialog-header">
      <h1 class="dialog_name">
        {{ isEditMode ? $t('information_about')+" " : $t('adding')+" " }}позиции{{
          isEditMode ? " " + VendorCode : ""
        }}
      </h1>
      <q-btn
        flat
        round
        icon="close"
        size="md"
        color="accent"
        :title="$t('close_label')"
        @click="close"
      />
    </div>

    <div class="dialog_content">
      <!-- Пока данные загружаются -->
      <div v-if="dataLoading" class="loading-container q-mt-sm">
        <div v-for="n in 6" :key="n" class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <!-- Жирный заголовок (короткая широкая полоска) -->
            <div class="skeleton skeleton-title"></div>

            <!-- Текстовый спан (длинная полоска) -->
            <div
              class="skeleton skeleton-text"
              :class="{
                'skeleton-text-short': n % 3 === 0,
                'skeleton-text-medium': n % 3 === 1,
              }"
            ></div>
          </div>
        </div>
      </div>

      <div v-else class="inner_container">
        <div class="field-group">
          <div class="bold-name">{{ $t('name_upcase') }}</div>
          <div
            class="row no-wrap"
            ref="scroll_container"
            style="overflow-x: auto"
          >
            <table class="dialog-table" ref="triggerEl">
              <parameter-inline
                v-if="contextParameterContainerEl"
                :is-root="true"
                :editable="CanWrite"
                is-table
                :bold-selected="true"
                :id="'parameter-dialog'"
                v-model="parameters"
                :token-styles="tokenStyles"
                :show-custom="false"
                :use-custom-hints="true"
                :context-trigger="contextParameterContainerEl"
                :create-mode="!isEditMode"
              >
              </parameter-inline>
            </table>
          </div>
        </div>

        <div v-if="positionAddContainer.PriceType === 2" class="field-group">
          <div class="bold-name">Цена за ед.</div>
          <native-input
            :ref="'price_input'"
            style="max-width: 245px"
            :model-value="positionAddContainer.Price"
            input-type="number"
            :show-border="true"
            :disabledField="!CanWrite"
            :edit-on-click="true"
            @update:modelValue="
              (newprice) => {
                changeItemPrice(newprice, this.positionAddContainer.PriceType);
                positionAddContainer.Price = newprice;
              }
            "
          />
        </div>
        <div class="field-group">
          <div class="bold-name">{{ $t('unit_of_measure') }}</div>
          <q-select
            flat
            v-model="positionAddContainer.MeasureUnitId"
            style="min-width: 250px"
            class="thin-input q-mr-xs"
            dropdown-icon="expand_more"
            fill-input
            :bottom-slots="false"
            dense
            outlined
            :disable="isEditMode || !CanWrite"
            input-debounce="500"
            emit-value
            map-options
            option-label="Name"
            option-value="Id"
            ref="measure_select"
            :options="
              storage_pos?.measureUnits ? storage_pos?.measureUnits.Hints : []
            "
          />
        </div>

        <div class="field-group">
          <div class="bold-name">{{ $t('price_calc_method') }}</div>
          <q-select
            flat
            style="min-width: 250px"
            v-model="currentPriceType"
            class="thin-input q-mb-xs q-mr-xs"
            fill-input
            :bottom-slots="false"
            dense
            outlined
            :disable="!CanWrite"
            :label-slot="false"
            hide-bottom-space
            dropdown-icon="expand_more"
            option-value="Id"
            option-label="Name"
            input-debounce="500"
            :ref="'price_select'"
            :options="priceOptions"
            @select="changeItemPrice(positionAddContainer.Price)"
          />
        </div>

        <div class="field-group">
          <div class="flex items-end" style="flex-wrap: nowrap">
            <div class="bold-name">{{ $t('comment') }}:</div>
            <span
              class="comment-tpl-trigger q-ml-sm"
              style="align-self: start"
              @click.stop
            >
              <app-icon
                name="comment-template"
                width="18px"
                :color="CanWrite ? '#979797' : '#c9c9c9'"
                :hover-color="CanWrite ? '#499259' : '#c9c9c9'"
                :cursor="CanWrite ? 'pointer' : 'default'"
                :title="$t('comment')"
              />
              <q-menu
                v-if="CanWrite"
                max-height="1300px"
                ref="comment_template_menu"
                anchor="center right"
                self="center left"
              >
                <aux-selector
                  :model-type="24"
                  :input-index="0"
                  :allow-multiple="false"
                  :allow-free-input="true"
                  @deleteItem="deleteCommentTemplate"
                  v-model="comment_templates"
                  @input-value="
                    (val) => {
                      addCommentTemplate(val);
                    }
                  "
                  @selectorClosed="
                    $refs.comment_template_menu &&
                      $refs.comment_template_menu.hide()
                  "
                />
              </q-menu>
            </span>
          </div>
          <native-input
            ref="comment_field"
            style="width: -moz-available; width: -webkit-fill-available"
            :show-border="true"
            :disabledField="!CanWrite"
            :model-value="positionAddContainer.Note"
            @update:caretPosition="onCaretPositionChange"
            @click="onNativeInputClick"
            @blur="onCommentBlur"
            :edit-on-click="true"
            @update:modelValue="
              (newname) => {
                changeItemNote(newname);
              }
            "
          />
        </div>
        <div class="field-group">
          <div class="bold-name flex items-center">
            {{ $t('files') }}
            <div class="file-button q-ml-sm">
              <input
                ref="fileInputStorage"
                type="file"
                multiple
                hidden
                @change="attachFileForPositionAdd"
              />
              <q-icon
                :name="fileIconName"
                size="xs"
                :disable="!CanWrite"
                color="accent"
                class="pointer hover-green"
                :title="$t('attach_files')"
                @click="chooseAndUploadFile"
              />
            </div>
          </div>
          <div class="file-list">
            <!-- 1. Уже привязанные файлы (с сервера) -->
            <div
              v-for="(file, index) in attachedFiles"
              :key="index"
              class="file-item"
            >
              <span
                class="file_container"
                :title="$t('upload_file')"
                @click.stop="DownloadFile(file, $event)"
              >
                {{ file.Name }}
              </span>
              <q-icon
                v-if="CanWrite"
                name="delete"
                size="16px"
                color="grey-7"
                class="pointer hover-green"
                :title="$t('remove')"
                @click="deleFileFromPositionAdd(file.Id)"
              />
            </div>

            <!-- 2. Новые файлы (из буфера) -->
            <div
              v-for="item in pendingFiles"
              :key="item.tempId"
              class="file-item"
            >
              <span class="pending"> {{ item.name }} ({{ $t('prepared') }}) </span>
              <q-icon
                v-if="CanWrite"
                name="delete"
                size="16px"
                color="grey-7"
                class="pointer hover-green"
                :title="$t('remove')"
                @click="removePending(item.tempId)"
              />
            </div>
          </div>
        </div>
        <div v-if="isEditMode" class="field-group q-my-xs">
          <div class="field-group__row">
            <div class="bold-name">{{ $t('amount_by_positions') }}:</div>
            <span>{{ money(currentTotalAmount) }}</span>
          </div>
        </div>

        <div
          v-if="currentMovements && currentMovements.length"
          class="field-group"
          style="width: 100%"
        >
          <table
            class="dialog-table"
            style="width: 100%; table-layout: auto !important"
          >
            <thead>
              <tr>
                <th>{{ $t('sku') }}</th>
                <th>{{ $t('date_label') }}</th>
                <th>{{ $t('from') }}/{{ $t('to_upper') }}</th>
                <th>{{ $t('responsible_worker') }}</th>
                <th>Кол-во<br />Цена/ед.<br />Итого</th>
                <th>{{ $t('bills') }}/проекты</th>
                <th>{{ $t('comment') }}</th>
              </tr>
            </thead>
            <tbody style="font-size: 14px">
              <tr
                v-for="(row_move, idx) in currentMovements"
                :key="'conn_line_' + idx"
              >
                <td style="width: 150px">
                  <div class="row no-wrap items-top">
                    <div style="display: flex; flex-direction: column">
                      <app-icon
                        v-if="row_move.Id"
                        class="item-move-info q-mx-xs"
                        name="info-circle-outline"
                        width="18px"
                        color="#979797"
                        hover-color="#499259"
                        cursor="pointer"
                        :title="$t('info')"
                        @click="
                          openInfoDialog(row_move.Id, row_move.MovementType)
                        "
                      />
                      <span
                        v-html="moveTypeName(row_move.MovementType)"
                        style="margin-left: 8px"
                      />
                    </div>
                    <div style="display: flex; flex-direction: column">
                      <span style="color: grey">
                        {{ row_move.VendorCode }}
                      </span>
                      <span
                        v-if="row_move.ItemsInMovementCount > 1"
                        style="font-size: 12px; font-weight: 500"
                      >
                        {{ $t('total') }} {{ row_move.ItemsInMovementCount }} {{ $t('pos_abbr') }}
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  <span>
                    {{ dateFormatted(row_move.Date) }}
                  </span>
                </td>
                <td>
                  <div
                    class="row no-wrap items-baseline"
                    style="justify-content: between"
                  >
                    <div
                      v-if="!isIncome(row_move.MovementType)"
                      style="width: min-content"
                    >
                      {{ row_move.SourceStorage?.Name }}
                    </div>
                    <div
                      v-if="isTransfer(row_move.MovementType)"
                      style="margin-left: 3px"
                      v-html="moveTypeName(row_move.MovementType)"
                    />
                    <div
                      v-if="!isExpense(row_move.MovementType)"
                      style="width: min-content"
                    >
                      {{ row_move.ReceiverStorage?.Name }}
                    </div>
                  </div>
                </td>
                <td>
                  <span>
                    {{ row_move.Creator?.Name }}
                  </span>
                </td>
                <td>
                  <span> {{ row_move.ItemQuantity }} </span><br />
                  <span> {{ row_move.SingleItemPrice }} </span><br />
                  <span>
                    {{ row_move.TotalAmount }}
                  </span>
                </td>
                <td>
                  <span v-if="row_move.Bills.length" style="font-weight: 500">
                    {{ $t('bills') }}:
                  </span>
                  <div v-for="(bill, bi) in row_move.Bills" :key="'imb-' + bi">
                    <!-- 1 = BillRowProps.BillLinkedBase -->
                    <bill-row :el="bill" :bill-type="1" />
                  </div>

                  <div
                    v-if="row_move.Projects.length"
                    :style="
                      'font-weight: 500;' +
                      (row_move.Bills.length ? 'margin-top: 8px;' : '')
                    "
                  >
                    {{ $t('projects_label') }}:
                  </div>
                  <div
                    v-for="(project, pi) in row_move.Projects"
                    :key="'imp-' + pi"
                  >
                    <!-- 1 = BillRowProps.BillLinkedBase -->
                    <project-row
                      class="q-mb-xs"
                      :el="project"
                      :hide-remove-btn="true"
                    />
                  </div>
                </td>
                <td>
                  <span>
                    {{ row_move.Note }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div v-if="!isEditMode" class="dialog-button">
      <div class="row no-wrap justify-end">
        <q-btn
          no-caps
          :label="isLoading ? $t('loading_short') : $t('save')"
          color="primary"
          @click="AddPosition"
          :disable="!creationAllowed || isLoading || !CanWrite"
        />
        <q-btn :label="$t('cancel_short')" no-caps flat @click="close" />
      </div>
    </div>
  </q-card>
</template>

<script lang="ts">
import { Vue, Component, Prop, Emit, Watch } from "vue-facing-decorator";
import { date, QBtn, QIcon, QSelect, useQuasar } from "quasar";
import { reactive } from "vue";
import {
  StorageItemPriceType,
  V2StorageItemMovementType,
} from "@/models/etc/types";
import {
  StorageItemAddRequest,
  StorageItemInfo,
  StorageNoteTemplateDto,
} from "@/models/interfaces/storage/storage-models";
import { storagePositionsStore } from "@/store/storage/storage-positions";
import { ParameterInstanceContainer } from "../../../models/interfaces/parameters/parameter-models";
import {
  isInstanceContainerEmpty,
  mapCreateContainerToInstance,
  mapFilledContainerFromInstance,
} from "../../../models/helpers/parameter-helpers";
import _ from "lodash";
import NativeInput from "../../common/basic/NativeInput.vue";
import {
  CommonEntity,
  SingleIdResponse,
} from "../../../models/interfaces/common";
import { auxStore } from "../../../store/auxillary";
import { StorageItemMovementInItemInfo } from "../../../models/interfaces/finances/finance-models";
import MoveInfoDialog from "./storageshared/MoveInfoDialog.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { storageStore } from "../../../store/storage/storage-instances";
import { i18n } from "../../../i18n";

interface PendingFile {
  tempId: string; // для ключей в v-for
  file: File; // сам объект файла для FormData
  name: string;
}

@Component({
  name: "ItemAdditionComponent",
  emits: ["close"],
  components: {
    QBtn,
    QIcon,
    QSelect,
    NativeInput,
    MoveInfoDialog,
    AppIcon,
  },
})
export default class ItemAdditionComponent extends Vue {
  @Prop({ type: Number, default: 0 }) existingPositionId!: number;

  // custom icons
  private attachIconUrl: string = require("@/assets/attach.svg");
  // вычисляемое свойство, которое Quasar ожидает в виде строки "img:URL"
  get fileIconName(): string {
    return `img:${this.attachIconUrl}`;
  }

  get isEditMode() {
    return this.existingPositionId > 0;
  }

  get CanWrite() {
    return this.storage.storageAccess?.CanWrite; 
  }

  contextParameterContainerEl: any = undefined;
  infoLoaded: boolean = false;
  isLoading: boolean = false;

  attachedFiles: CommonEntity[] = reactive([]);
  pendingFiles = reactive<PendingFile[]>([]);
  comment_templates = reactive([]);
  lastCommentCaretPosition = 0;
  private _blockCaretUpdate = false;
  private initialNoteAtOpen = "";
  private noteSaveHandled = false;

  parameters = reactive<ParameterInstanceContainer[]>([]);
  tokenStyles: Record<string, string> = {
    artikul: "tok--link",
    name: "tok--base",
    class: "tok--muted",
  };

  private $q = useQuasar();

  // stores import
  storage_pos: any | undefined = undefined;
  auxstore: any | undefined = undefined;
  storage: any | undefined = undefined;

  creationAllowed: boolean = false;
  VendorCode: string = "";
  currentTotalAmount: number = 0;
  dataLoading: boolean = false;

  currentMovements: StorageItemMovementInItemInfo[] = reactive([]);

  private lastScrollLeft = 0;
  private resizeObserver: ResizeObserver | null = null;

  // money display value formatting
  money(n: number) {
    return new Intl.NumberFormat("ru-RU", {
      // style: "currency",
      // currency: "RUB",
      minimumFractionDigits: 0, // Не показывать нули, если дробной части нет
      maximumFractionDigits: 4,
    }).format(n || 0);
  }

  openInfoDialog(id: number, type: V2StorageItemMovementType) {
    this.$q
      .dialog({
        component: MoveInfoDialog,
        componentProps: {
          existingMovementId: id,
          createType: type,
        },
      })
      .onOk(() => {
        console.log(
          "Диалог успешно закрыт (пользователь сохранил или просто закрыл)"
        );
        // Здесь можно обновить список, перезагрузить данные и т.д.
      })
      .onCancel(() => {
        console.log("Отмена (если добавишь onDialogCancel)");
      })
      .onDismiss(() => {
        console.log("Диалог закрыт любым способом");
      });
  }

  positionAddContainer: StorageItemAddRequest = reactive({
    //список заполненных контейнеров в т.ч. для создания заполненного вхождения параметров.
    //один для одиночного вхождения, несколько - для параметра списка.
    Containers: [],
    MeasureUnitId: 0,
    Note: "",
    Files: [],
    PriceType: StorageItemPriceType.SetByUser,
    Price: 0,
    UsedNoteTemplateIds: [],
  });

  currentPriceType: CommonEntity = {
    Name: i18n.global.t('set_by_user'),
    Id: StorageItemPriceType.SetByUser,
  };

  dateFormatted(val: string): string {
    return date.formatDate(new Date(val), "DD.MM.YY");
  }

  created() {
    this.storage = storageStore();
    this.storage_pos = storagePositionsStore();
    this.auxstore = auxStore();

    this.attachedFiles = reactive([]);
    this.pendingFiles = reactive([]);
    this.positionAddContainer.MeasureUnitId =
      this.storage_pos?.measureUnits?.Hints[0].Id;

    this.item_container_getter();
  }

  mounted() {
    this.contextParameterContainerEl = this.$refs.triggerEl;
    this.preloadExistingPosition();

    const container = this.$refs.scroll_container as HTMLElement;
    // @ts-ignore
    const content = this.$refs.triggerEl as HTMLElement; // или конкретный внутренний div

    // 1. Следим за позицией скролла вручную
    container.addEventListener("scroll", () => {
      if (container.scrollLeft !== 0) {
        this.lastScrollLeft = container.scrollLeft;
      }
    });

    // 2. Следим за изменением размеров контента
    this.resizeObserver = new ResizeObserver(() => {
      // Как только контент перерисовался и сменил размер — возвращаем скролл
      if (container.scrollLeft !== this.lastScrollLeft) {
        container.scrollLeft = this.lastScrollLeft;
      }
    });

    this.resizeObserver.observe(content);
  }

  // for template
  isIncome(mtype: V2StorageItemMovementType) {
    return mtype === V2StorageItemMovementType.In;
  }

  isExpense(mtype: V2StorageItemMovementType) {
    return mtype === V2StorageItemMovementType.Out;
  }

  isTransfer(mtype: V2StorageItemMovementType) {
    return mtype === V2StorageItemMovementType.Inner;
  }

  async item_container_getter() {
    const val = await this.storage_pos?.getStorageItemNameContainer();
    const that = this;
    if (val && val.ParameterContainer && !this.isEditMode) {
      that.parameters = mapCreateContainerToInstance(val.ParameterContainer);
    }
  }

  @Watch("positionAddContainer", { deep: true })
  container_watcher() {
    // console.log(this.positionAddContainer)
  }

  @Watch("parameters", { deep: true })
  parameter_watcher(val: ParameterInstanceContainer[]) {
    // if (!this.isEditMode) {
    this.positionAddContainer.Containers = mapFilledContainerFromInstance(val);
    // }
    this.creationAllowed = false;
    for (let vi = 0; vi < val.length; vi++) {
      this.creationAllowed =
        this.creationAllowed || !isInstanceContainerEmpty(val[vi]); // at least one should be non-empty
    }
  }

  onCaretPositionChange(val: number) {
    if (val >= 0 && !this._blockCaretUpdate) {
      this.lastCommentCaretPosition = +val;
    }
  }

  onCommentBlur() {
    if (!this.CanWrite) return;
    this._blockCaretUpdate = true;
    setTimeout(() => {
      this._blockCaretUpdate = false;
    }, 0);
  }

  onNativeInputClick() {
    if (!this.CanWrite) return;
    setTimeout(() => {
      const ref = this.$refs.comment_field as any;
      const field = ref?.$refs?.editable_field as HTMLElement | null;
      if (!field) return;
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const pre = range.cloneRange();
        pre.selectNodeContents(field);
        pre.setEnd(range.endContainer, range.endOffset);
        this.lastCommentCaretPosition = pre.toString().length;
      }
    }, 0);
  }

  @Watch("comment_templates")
  comment_template_watcher() {
    if (!this.comment_templates.length) {
      return;
    }

    const templateText = (this.comment_templates[0] as StorageNoteTemplateDto).Text;
    const original = this.positionAddContainer.Note || "";

    this.positionAddContainer.UsedNoteTemplateIds.push((this.comment_templates[0] as StorageNoteTemplateDto).Id);

    let c_pos = this.lastCommentCaretPosition;
    if (c_pos < 0) {
      c_pos = 0;
    } else if (c_pos > original.length) {
      c_pos = original.length;
    }

    this._blockCaretUpdate = true;
    this.positionAddContainer.Note =
      original.slice(0, c_pos) + templateText + original.slice(c_pos);
    this.lastCommentCaretPosition = c_pos + templateText.length;
    this.comment_templates.splice(0);

    // @ts-ignore
    this.$refs.comment_template_menu && this.$refs.comment_template_menu.hide();
    this.$nextTick(() => {
      this._blockCaretUpdate = false;
    });
  }

  async addCommentTemplate(t_name: string) {
    if (
      t_name &&
      t_name.length &&
      this.positionAddContainer.Note &&
      this.positionAddContainer.Note.length
    ) {
      console.log("add new: " + t_name);
      this.storage_pos.createNoteTemplate({
        Name: t_name,
        Text: this.positionAddContainer.Note,
      });
    }
  }

  async deleteCommentTemplate(t_id: number) {}

  // width function
  get priceOptions() {
    return [
      {
        Name: this.$t('price_last_receipt'),
        Id: StorageItemPriceType.LastIncome,
      },
      {
        Name: this.$t('set_by_user'),
        Id: StorageItemPriceType.SetByUser,
      },
    ];
  }

  // preload existing position
  async preloadExistingPosition() {
    if (this.isEditMode) {
      this.dataLoading = true;
      const existing_pos: StorageItemInfo =
        await this.storage_pos.getStorageItemInfo(this.existingPositionId);

      if (existing_pos && existing_pos.NameParameters.length) {
        this.parameters = reactive(_.cloneDeep(existing_pos.NameParameters));
      }

      this.positionAddContainer.MeasureUnitId = existing_pos.MeasureUnit.Id;
      this.positionAddContainer.Note = existing_pos.Note;
      this.positionAddContainer.Price = existing_pos.Price;
      this.attachedFiles = existing_pos.Files;
      this.currentPriceType = this.priceOptions.find(
        (p) => p.Id === existing_pos.PriceType
      ) || {
        Name: this.$t('set_by_user'),
        Id: StorageItemPriceType.SetByUser,
      };
      this.positionAddContainer.PriceType = this.currentPriceType.Id;
      this.VendorCode = existing_pos.VendorCode;
      this.currentTotalAmount = existing_pos.TotalAmount;
      this.currentMovements = existing_pos.Movements;
      this.initialNoteAtOpen = existing_pos.Note || "";
      this.positionAddContainer.UsedNoteTemplateIds.splice(0);
      this.lastCommentCaretPosition = 0;
      this.noteSaveHandled = false;

      this.infoLoaded = true;
      this.dataLoading = false;
    }
  }

  // movement type icon span
  moveTypeName(arg: number): string {
    switch (arg) {
      // case MovementTypeFilterFlagsDto.Both:
      // return this.$t('both_label')
      case V2StorageItemMovementType.Out:
        return '<span class="q-mr-xs no-break movement-element movement-element--expense"/>';
      case V2StorageItemMovementType.In:
        return '<span class="q-mr-xs no-break movement-element movement-element--income"/>';
      case V2StorageItemMovementType.Inner:
        return `<span class="q-mr-xs no-break movement-element movement-element--transfer"/>`;
      default:
        return this.$t("filter_empty");
    }
  }

  @Watch("currentPriceType", { deep: true })
  price_watcher() {
    this.changeItemPrice(
      this.positionAddContainer.Price,
      this.currentPriceType.Id
    );
    this.positionAddContainer.PriceType = this.currentPriceType.Id;
  }

  async changeItemPrice(newvalue: number, new_type: number) {
    if (
      !this.infoLoaded ||
      !this.isEditMode ||
      (this.positionAddContainer.Price === (new_type === 2 ? newvalue : 0) &&
        this.positionAddContainer.PriceType === new_type)
    ) {
      return;
    }

    const request = {
      ItemId: this.existingPositionId, // ID позиции или движения
      Price: new_type === 2 ? newvalue : 0, // строка для замены / добавления комментария
      PriceType: new_type,
    };
    await this.storage_pos.editStorageItemPriceSettings(request);
  }

  async changeItemNote(newname: string) {
    this.positionAddContainer.Note = newname;
  }

  async saveCommentOnCloseIfNeeded() {
    if (this.noteSaveHandled) return;
    this.noteSaveHandled = true;

    if (!this.isEditMode || !this.infoLoaded || !this.CanWrite) return;

    const note = this.positionAddContainer.Note || "";
    const noteChanged = note !== this.initialNoteAtOpen;
    const hasTemplateIds = this.positionAddContainer.UsedNoteTemplateIds.length > 0;
    if (!noteChanged && !hasTemplateIds) return;

    const request = {
      ElementId: this.existingPositionId,
      Value: note,
      UsedNoteTemplateIds: this.positionAddContainer.UsedNoteTemplateIds,
    };
    const res = await this.storage_pos.editStorageItemNote(request);
    if (res?.response_ok) {
      this.initialNoteAtOpen = note;
      this.positionAddContainer.UsedNoteTemplateIds.splice(0);
      this.lastCommentCaretPosition = 0;
    } else {
      this.noteSaveHandled = false;
    }
  }

  @Emit("attach-file")
  async attachFileForPositionAdd(event: Event) {
    if (!this.CanWrite) { return }

    const target = event.target as HTMLInputElement;
    if (!target.files) return;

    const selected = Array.from(target.files).map((f) => ({
      tempId: Math.random().toString(36).slice(2),
      file: f,
      name: f.name,
    }));

    this.pendingFiles.push(...selected);
    target.value = ""; // сброс инпута

    // если позиция уже существует, то сразу же загружаем файл
    if (this.isEditMode) {
      this.isLoading = true;
      try {
        // Загружаем все файлы из буфера по очереди (или через Promise.all)
        const uploadPromises = this.pendingFiles.map((item) => {
          // возвращает Id загруженного и привязанного файла
          return this.storage_pos
            .addFileToStorageItem(item.file, this.existingPositionId)
            .then((r: SingleIdResponse) => {
              return {
                Id: r?.Id,
                Name: item.name,
              } as CommonEntity;
            });
        });

        const newFiles = await Promise.all(uploadPromises);

        // 2. Формируем итоговый массив ID (старые + только что загруженные)
        this.attachedFiles = reactive([...this.attachedFiles, ...newFiles]);

        this.isLoading = false;

        // Очищаем буфер после успеха
        this.pendingFiles.splice(0);
      } catch (e) {
        alert(this.$t('error_saving'));
      } finally {
        this.isLoading = false;
      }
    }
  }

  // Удаление локального (еще не загруженного) файла
  removePending(tempId: string) {
    if (this.isLoading) {
      return;
    }
    this.pendingFiles.splice(
      this.pendingFiles.findIndex((f) => f.tempId === tempId),
      1
    );
  }

  @Emit("choose-upload")
  chooseAndUploadFile() {
    if (!this.CanWrite) { return }

    if (this.$refs.fileInputStorage) {
      // @ts-ignore
      this.$refs.fileInputStorage.click();
    }
  }

  @Emit("download-file")
  DownloadFile(file: CommonEntity, event: Event) {
    if (this.auxstore) {
      this.auxstore.downloadFile(file.Id, file.Name);
    }
  }

  @Emit("delete-file")
  async deleFileFromPositionAdd(fileId: string) {
    if (this.isLoading) {
      return;
    }
    try {
      await this.storage_pos.deleteFileFromStorageItem(fileId);
      this.attachedFiles.splice(
        this.attachedFiles.findIndex((f) => f.Id === fileId),
        1
      );
    } catch (e) {
      console.error(this.$t('error_deleting_file'), e);
    }
  }

  @Emit("add-position")
  async AddPosition() {
    if (this.isEditMode) {
      return;
    }

    this.isLoading = true;
    try {
      // возвращает Id новой позиции
      const newStoragePosId = (
        await this.storage_pos.createStorageItem(this.positionAddContainer)
      )?.Id;

      if (newStoragePosId > 0) {
        // Загружаем все файлы из буфера по очереди (или через Promise.all)
        const uploadPromises = this.pendingFiles.map((item) => {
          // возвращает Id загруженного и привязанного файла
          return this.storage_pos.addFileToStorageItem(
            item.file,
            newStoragePosId
          );
        });

        await Promise.all(uploadPromises);

        this.isLoading = false;
        this.close();
        // Очищаем буфер после успеха
        this.pendingFiles.splice(0);
        this.positionAddContainer.UsedNoteTemplateIds.splice(0);
      }
    } catch (e) {
      alert(this.$t('error_saving'));
    } finally {
      this.isLoading = false;
    }
  }

  async close() {
    await this.saveCommentOnCloseIfNeeded();
    this.$emit("close", null);
  }

  beforeUnmount() {
    void this.saveCommentOnCloseIfNeeded();
  }
}
</script>

<style lang="scss" scoped>
.comment-tpl-trigger {
  display: inline-flex;
  line-height: 0;
}

.item-move-info {
  margin-top: -1px;
  margin-left: 2px;
}
</style>
